// src/App.tsx
import React, { useEffect, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';

// ====== IMPORTS REGULARES ======
// Componentes carregados imediatamente (necessários para PWA e guards)
import { getClientId } from './utils/clientId';
import InstallApp from './components/installApp';
import InstallAppIOS from './components/installAppIOS';
import Loader from './components/loader';

import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import PrivateRoute from './components/guard/index';
import ErrorBoundary from './components/ErrorBoundary';
import { lazyRetry as lazy } from './utils/lazyWithRetry';
import { usePushNotifications } from './hooks/usePushNotifications';
import { Analytics } from "@vercel/analytics/react"
import { SpeedInsights } from "@vercel/speed-insights/react"
import { ToastProvider } from './context/ToastContext';
import { APP_ROUTES } from './utils/routes';


import './App.css';

// ====== LAZY LOADING DE PÁGINAS ======
// Páginas são carregadas sob demanda, reduzindo o bundle inicial

// Páginas Públicas
const Home = lazy(() => import('./pages/Home/Home'));
const FieldDetails = lazy(() => import('./pages/FieldDetails/FieldDetails'));
const Contact = lazy(() => import('./pages/Contact/Contact'));
const Login = lazy(() => import('./pages/Login/Login'));
const LoginSuccess = lazy(() => import('./pages/LoginSuccess/LoginSuccess'));
const Register = lazy(() => import(/* webpackChunkName: "RegisterPageV2" */ './pages/Register/Register'), 'Register');
const TermsOfUse = lazy(() => import('./pages/TermsOfUse/TermsOfUse'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword/ResetPassword'));
const VideoShare = lazy(() => import('./pages/VideoShare/VideoShare'));
const Testes = lazy(() => import('./pages/Testes/Testes'));

// Páginas Privadas
const Profile = lazy(() => import('./pages/Profile/Profile'));
const Notifications = lazy(() => import('./pages/Notifications/Notifications'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const FieldCourts = lazy(() => import('./pages/FieldCourts/FieldCourts'));
const MemberManagement = lazy(() => import('./pages/MemberManagement/MemberManagement'));
const Favorites = lazy(() => import('./pages/Favorites/Favorites'));

// Páginas Admin
const AdminDashboard = lazy(() => import('./pages/Admin/Dashboard/AdminDashboard'));
const AdminFields = lazy(() => import('./pages/Admin/Fields/AdminFields'));
const AdminFieldUsers = lazy(() => import('./pages/Admin/FieldUsers/AdminFieldUsers'));
const AdminFieldMetrics = lazy(() => import('./pages/Admin/FieldMetrics/AdminFieldMetrics'));
const AdminRegisteredFields = lazy(() => import('./pages/Admin/RegisteredFields/AdminRegisteredFields'));
const AdminGlobalMetrics = lazy(() => import('./pages/Admin/GlobalMetrics/AdminGlobalMetrics'));
const AdminUsers = lazy(() => import('./pages/Admin/Users/AdminUsers'));
const AdminBillingCosts = lazy(() => import('./pages/Admin/BillingCosts/AdminBillingCosts'));

const PushNotificationsBootstrap: React.FC = () => {
  usePushNotifications();
  return null;
};

const INSTALL_PROMPT_DISABLED_PATHS = new Set<string>([APP_ROUTES.LANDING]);

const isStandalonePwa = (): boolean => {
  if (typeof window === 'undefined') return false;

  const navigatorWithStandalone = window.navigator as Navigator & { standalone?: boolean };

  return window.matchMedia('(display-mode: standalone)').matches ||
    navigatorWithStandalone.standalone === true;
};

const PwaLaunchRedirect: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (pathname === APP_ROUTES.LANDING && isStandalonePwa()) {
      navigate(APP_ROUTES.SEARCH, { replace: true });
    }
  }, [navigate, pathname]);

  return null;
};

const PwaInstallers: React.FC = () => {
  const { pathname } = useLocation();
  const disabled = INSTALL_PROMPT_DISABLED_PATHS.has(pathname);

  return (
    <>
      <InstallApp disabled={disabled} />
      <InstallAppIOS disabled={disabled} />
    </>
  );
};


const App: React.FC = () => {
  /* gera clientId no primeiro mount */
  useEffect(() => {
    console.log('%c [App] Version 1.0.4 - Layout & Fix Update ', 'background: #222; color: #bada55; font-size: 20px;');
    // Gera ou recupera o clientId assim que a app for montada
    getClientId();
  }, []);

  return (
    <Router>
      <PwaLaunchRedirect />
      <ToastProvider>
        <AuthProvider>
          <PushNotificationsBootstrap />
          <NotificationProvider>
            <ErrorBoundary>
              <Suspense fallback={<Loader />}>
                <Routes>
                {/* ---------- PÚBLICAS ---------- */}
                <Route path={APP_ROUTES.LANDING} element={<Testes />} />
                <Route path={APP_ROUTES.SEARCH} element={<Home />} />
                <Route path="/field/:name" element={<FieldDetails />} />
                {/* <Route path="/video-details/:id" element={<VideoDetails />} /> REMOVIDO POIS AGORA USAMOS /share/:id */}
                <Route path={APP_ROUTES.MAIL} element={<Contact />} />
                <Route path={APP_ROUTES.LOGIN} element={<Login />} />
                <Route path={APP_ROUTES.LOGIN_SUCCESS} element={<LoginSuccess />} />
                <Route path={APP_ROUTES.TERMS} element={<TermsOfUse />} />
                <Route path={APP_ROUTES.PRIVACY} element={<TermsOfUse isPrivacy />} />
                <Route path={APP_ROUTES.REGISTER} element={<Register />} />
                <Route path={APP_ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
                <Route path={APP_ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
                <Route path="/share/:id" element={<VideoShare />} />

                {/* ---------- PRIVADAS ( hibrido ) ---------- */}
                <Route element={<PrivateRoute roles={['manager', 'user', 'admin']} />}>
                  <Route path={APP_ROUTES.PROFILE} element={<Profile />} />
                  <Route path={APP_ROUTES.NOTIFICATIONS} element={<Notifications />} />
                  <Route path={APP_ROUTES.FAVORITES} element={<Favorites />} />
                </Route>

                {/* ---------- PRIVADAS (somente manager) ---------- */}
                <Route element={<PrivateRoute roles={['manager']} />}>
                  <Route path={APP_ROUTES.DASHBOARD} element={<Dashboard />} />
                  <Route path={APP_ROUTES.DASHBOARD_FIELD} element={<FieldCourts />} />
                  <Route path={APP_ROUTES.DASHBOARD_FIELD_MEMBERS} element={<MemberManagement />} />
                </Route>

                {/* ---------- PRIVADAS (somente admin) ---------- */}
                <Route element={<PrivateRoute roles={['admin']} />}>
                  <Route path={APP_ROUTES.ADMIN_DASHBOARD} element={<AdminDashboard />} />
                  <Route path={APP_ROUTES.ADMIN_FIELDS} element={<AdminFields />} />
                  <Route path="/admin/fields/:fieldId/users" element={<AdminFieldUsers />} />
                  <Route path="/admin/fields/:fieldId/metrics" element={<AdminFieldMetrics />} />
                  <Route path="/admin/fields/registered" element={<AdminRegisteredFields />} />
                  <Route path={APP_ROUTES.ADMIN_USERS} element={<AdminUsers />} />
                  <Route path={APP_ROUTES.ADMIN_METRICS} element={<AdminGlobalMetrics />} />
                  <Route path={APP_ROUTES.ADMIN_BILLING} element={<AdminBillingCosts />} />
                </Route>

                {/* ---------- Fallback ---------- */}
                <Route path={APP_ROUTES.UNAUTHORIZED} element={<p>Acesso negado</p>} />
                <Route path="*" element={<Navigate to={APP_ROUTES.LANDING} replace />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>

            {/* instaladores PWA */}
            <PwaInstallers />
          </NotificationProvider>
        </AuthProvider>
      </ToastProvider>

      {/* vercel analisis */}
      <Analytics />
      <SpeedInsights />
    </Router>
  );
};

export default App;
